import {
  AfterViewInit,
  Component,
  Inject,
  InjectionToken,
  Injector,
  inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';
import {
  createUnicornShownProperties,
  createUnicornIncidentCode,
  createUnicornRecoveryAttemptProperties,
  createUnicornRecoveryOutcomeProperties,
  getUnicornRecoveryGuardKey,
  isUnicornCause,
  normalizeUnicornBuildIdentity,
  parseUnicornPendingRecovery,
  UNICORN_CAUSE_STATE_KEY,
  UNICORN_PRODUCER_STATE_KEY,
  UNICORN_RECOVERY_GUARD_PREFIX,
  UNICORN_RECOVERY_GUARD_TTL_MS,
  UNICORN_RECOVERY_PENDING_KEY,
  UNICORN_RECOVERY_PENDING_TTL_MS,
  UnicornCause,
  UnicornCleanupResult,
  UnicornConnectivityState,
  UnicornPendingRecovery,
  UnicornRecoveryAction,
  UnicornRecoveryRung,
  UnicornServiceWorkerState,
  UnicornStalenessVerdict,
} from 'src/app/core/domain/unicorn';

export const UNICORN_PROBE_TIMEOUT_MS = new InjectionToken<number>(
  'UNICORN_PROBE_TIMEOUT_MS',
  { providedIn: 'root', factory: () => 3000 }
);

export const UNICORN_INCIDENT_CODE_FACTORY = new InjectionToken<() => string>(
  'UNICORN_INCIDENT_CODE_FACTORY',
  { providedIn: 'root', factory: () => createUnicornIncidentCode }
);

export const UNICORN_REAUTHENTICATE = new InjectionToken<
  (destination: string) => Promise<boolean>
>(
  'UNICORN_REAUTHENTICATE',
  {
    providedIn: 'root',
    factory: () => {
      const injector = inject(Injector);
      return async (destination: string): Promise<boolean> => {
        const auth = await import('src/app/core/auth/interactive-reauth');
        const authServiceModule = await import('@auth0/auth0-angular');
        const authService = injector.get(authServiceModule.AuthService);
        auth.initiateInteractiveReauth(authService, destination);
        return true;
      };
    },
  }
);

export const UNICORN_CONNECTIVITY_PROBE_URL = new InjectionToken<
  () => Promise<string>
>(
  'UNICORN_CONNECTIVITY_PROBE_URL',
  {
    providedIn: 'root',
    factory: () => async (): Promise<string> => {
      const { environment } = await import('src/environments/environment');
      return `${environment.apiUrl}/public/stats`;
    },
  }
);

interface RecoveryOption {
  action: UnicornRecoveryAction;
  rung: UnicornRecoveryRung;
  label: string;
  destination: string;
}

type FatalPageLocale = 'en' | 'es';

interface FatalPageCopy {
  logoAlt: string;
  heading: string;
  cause: Record<UnicornCause, string>;
  imageAlt: string;
  incidentHeading: string;
  copyCode: string;
  incidentHelp: string;
  recoveryOptionsLabel: string;
  recoveryLabels: {
    updateAndContinue: string;
    restartAndContinue: string;
    checkConnectionAndRetry: string;
    tryLoadingAgain: string;
    signInAgain: string;
    reloadWithoutCleanup: string;
    resetSessionAndStartOver: string;
    checkConnectionAgain: string;
    continueToPublicSearch: string;
    reloadApplication: string;
  };
  recoveryMessages: {
    actionUnavailable: string;
    incidentCodeCopied: string;
    copyUnavailable: string;
    connectionUnavailable: string;
    actionIncomplete: string;
  };
}

export const FATAL_PAGE_COPY = {
  en: {
    logoAlt: 'rettX logo',
    heading: 'We hit a problem',
    cause: {
      'stale-build': 'A newer version is available. Update the app to continue.',
      auth: 'Your sign-in session needs to be renewed.',
      'data-load': 'We could not load the information you requested.',
      network: 'Your connection appears to be unavailable.',
      unknown: 'The application could not continue safely.',
    },
    imageAlt: 'Surprised unicorn',
    incidentHeading: 'Incident code',
    copyCode: 'Copy code',
    incidentHelp: 'Support can use this code to find the exact incident.',
    recoveryOptionsLabel: 'Recovery options',
    recoveryLabels: {
      updateAndContinue: 'Update and continue',
      restartAndContinue: 'Restart and continue',
      checkConnectionAndRetry: 'Check connection and retry',
      tryLoadingAgain: 'Try loading again',
      signInAgain: 'Sign in again',
      reloadWithoutCleanup: 'Reload without cleanup',
      resetSessionAndStartOver: 'Reset session and start over',
      checkConnectionAgain: 'Check connection again',
      continueToPublicSearch: 'Continue to public search',
      reloadApplication: 'Reload application',
    },
    recoveryMessages: {
      actionUnavailable:
        'That recovery action is unavailable. Please use the other option.',
      incidentCodeCopied: 'Incident code copied.',
      copyUnavailable:
        'Copy is unavailable. You can read the incident code aloud.',
      connectionUnavailable:
        'The connection is still unavailable. Your incident code remains on screen.',
      actionIncomplete:
        'That action did not complete. Please use the other recovery option.',
    },
  },
  es: {
    logoAlt: 'Logotipo de rettX',
    heading: 'Se ha producido un problema',
    cause: {
      'stale-build':
        'Hay una versión más reciente disponible. Actualiza la aplicación para continuar.',
      auth: 'Debes renovar tu sesión.',
      'data-load': 'No hemos podido cargar la información que has solicitado.',
      network: 'Parece que no hay conexión disponible.',
      unknown: 'La aplicación no ha podido continuar de forma segura.',
    },
    imageAlt: 'Unicornio sorprendido',
    incidentHeading: 'Código del incidente',
    copyCode: 'Copiar código',
    incidentHelp:
      'El equipo de soporte puede usar este código para encontrar el incidente exacto.',
    recoveryOptionsLabel: 'Opciones de recuperación',
    recoveryLabels: {
      updateAndContinue: 'Actualizar y continuar',
      restartAndContinue: 'Reiniciar y continuar',
      checkConnectionAndRetry: 'Comprobar la conexión y reintentar',
      tryLoadingAgain: 'Intentar cargar de nuevo',
      signInAgain: 'Iniciar sesión de nuevo',
      reloadWithoutCleanup: 'Recargar sin limpiar',
      resetSessionAndStartOver: 'Restablecer la sesión y empezar de nuevo',
      checkConnectionAgain: 'Comprobar la conexión de nuevo',
      continueToPublicSearch: 'Continuar a la búsqueda pública',
      reloadApplication: 'Recargar la aplicación',
    },
    recoveryMessages: {
      actionUnavailable:
        'Esta acción de recuperación no está disponible. Usa la otra opción.',
      incidentCodeCopied: 'Código del incidente copiado.',
      copyUnavailable:
        'No se puede copiar. Puedes leer en voz alta el código del incidente.',
      connectionUnavailable:
        'La conexión sigue sin estar disponible. El código del incidente permanece en pantalla.',
      actionIncomplete:
        'La acción no se ha completado. Usa la otra opción de recuperación.',
    },
  },
} satisfies Record<FatalPageLocale, FatalPageCopy>;

const FATAL_PAGE_LANGUAGE_ATTRIBUTE = 'data-rettx-language';
const FATAL_PAGE_LANGUAGE_PREFERENCE_KEY = 'rettx_landing_lang';

function normalizeFatalPageLocale(value: unknown): FatalPageLocale | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const baseLanguage = value.trim().toLowerCase().replace(/_/g, '-')
    .split('-', 1)[0];
  return baseLanguage === 'en' || baseLanguage === 'es'
    ? baseLanguage
    : undefined;
}

@Component({
  selector: 'app-error-page',
  standalone: true,
  imports: [],
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.scss']
})
export class PageGlobalErrorComponent implements AfterViewInit {
  private eventEmitted = false;
  private eventSent = false;
  private documentNavigationInitiated = false;
  private readonly recoveryInProgress = new Set<UnicornRecoveryRung>();
  private readonly recoveryPageHideListeners =
    new Map<UnicornRecoveryRung, EventListener>();
  private readonly handoffState: Record<string, unknown> | undefined;
  private readonly unicornShownAt: number;
  readonly incidentCode: string;
  readonly copy: FatalPageCopy;
  cause: UnicornCause;
  staleness: UnicornStalenessVerdict = 'unresolved';
  serviceWorker: UnicornServiceWorkerState = 'unresolved';
  connectivity: UnicornConnectivityState;
  recoveryMessage = '';

  constructor(
    private injector: Injector,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.copy = FATAL_PAGE_COPY[this.resolveLocale()];
    this.handoffState = this.getHandoffState();
    this.cause = isUnicornCause(
      this.handoffState?.[UNICORN_CAUSE_STATE_KEY]
    )
      ? this.handoffState[UNICORN_CAUSE_STATE_KEY]
      : 'unknown';
    this.incidentCode = this.getIncidentCode();
    this.unicornShownAt = this.getNow();
    this.connectivity = this.getConnectivityState();
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.eventEmitted) {
      return;
    }
    this.eventEmitted = true;

    const buildIdentity = this.getRunningBuildIdentity();
    const nativeState = this.getNativeState();
    const emit = (): void => {
      if (this.eventSent) {
        return;
      }
      this.eventSent = true;
      this.removePageHideListener(onPageHide);

      const properties = createUnicornShownProperties(
        this.handoffState?.[UNICORN_CAUSE_STATE_KEY],
        this.handoffState?.[UNICORN_PRODUCER_STATE_KEY],
        buildIdentity,
        this.staleness,
        this.serviceWorker,
        this.connectivity,
        this.incidentCode,
      );
      try {
        this.injector.get(AppInsightsService).trackEvent(
          'unicorn_shown',
          properties
        );
      } catch {
        // The fatal-error surface must not recurse if telemetry is unavailable.
      }
    };
    const onPageHide = (): void => emit();
    this.addPageHideListener(onPageHide);

    const stalenessProbe = this.getStalenessVerdict(nativeState, buildIdentity)
      .then(value => {
        this.staleness = value;
      })
      .catch(() => {
        this.staleness = 'unresolved';
      });
    const serviceWorkerProbe = this.getServiceWorkerState(nativeState)
      .then(value => {
        this.serviceWorker = value;
      })
      .catch(() => {
        this.serviceWorker = 'unresolved';
      });
    await Promise.all([stalenessProbe, serviceWorkerProbe]);
    emit();
  }

  get primaryRecovery(): RecoveryOption {
    if (this.requiresHardRecovery()) {
      return {
        action: 'hard-refresh',
        rung: 'primary',
        label: this.cause === 'stale-build' || this.staleness === 'stale'
          ? this.copy.recoveryLabels.updateAndContinue
          : this.copy.recoveryLabels.restartAndContinue,
        destination: '/patients',
      };
    }
    if (this.cause === 'network') {
      return {
        action: 'network-retry',
        rung: 'primary',
        label: this.copy.recoveryLabels.checkConnectionAndRetry,
        destination: '/patients',
      };
    }
    if (this.cause === 'data-load') {
      return {
        action: 'plain-refresh',
        rung: 'primary',
        label: this.copy.recoveryLabels.tryLoadingAgain,
        destination: '/patients',
      };
    }
    return {
      action: 'interactive-reauth',
      rung: 'primary',
      label: this.copy.recoveryLabels.signInAgain,
      destination: '/patients',
    };
  }

  get fallbackRecovery(): RecoveryOption {
    if (this.requiresHardRecovery()) {
      return {
        action: 'plain-refresh',
        rung: 'fallback',
        label: this.copy.recoveryLabels.reloadWithoutCleanup,
        destination: '/patients',
      };
    }
    if (this.cause === 'auth') {
      return {
        action: 'reset-auth-session',
        rung: 'fallback',
        label: this.copy.recoveryLabels.resetSessionAndStartOver,
        destination: '/',
      };
    }
    if (this.cause === 'network') {
      return {
        action: 'network-retry',
        rung: 'fallback',
        label: this.copy.recoveryLabels.checkConnectionAgain,
        destination: '/patients',
      };
    }
    if (this.cause === 'data-load') {
      return {
        action: 'public-search',
        rung: 'fallback',
        label: this.copy.recoveryLabels.continueToPublicSearch,
        destination: '/search',
      };
    }
    return {
      action: 'plain-refresh',
      rung: 'fallback',
      label: this.copy.recoveryLabels.reloadApplication,
      destination: '/patients',
    };
  }

  recover(rung: UnicornRecoveryRung): void {
    const option = rung === 'primary'
      ? this.primaryRecovery
      : this.fallbackRecovery;
    if (
      this.recoveryInProgress.has(rung) ||
      !this.persistRecoveryGuard(rung)
    ) {
      this.recoveryMessage = this.copy.recoveryMessages.actionUnavailable;
      return;
    }
    this.recoveryInProgress.add(rung);

    const pending: UnicornPendingRecovery = {
      incidentCode: this.incidentCode,
      cause: this.cause,
      action: option.action,
      rung,
      cleanup: 'unresolved',
      startedAt: this.getNow(),
      unicornShownAt: this.unicornShownAt,
      destination: option.destination,
    };
    const measurable = this.persistPendingRecovery(pending);
    const attemptProperties = createUnicornRecoveryAttemptProperties(
      this.incidentCode,
      this.cause,
      option.action,
      rung,
    );
    let attemptEmitted = false;
    const emitAttempt = (): void => {
      if (attemptEmitted) {
        return;
      }
      attemptEmitted = true;
      this.trackEventSafely(
        'unicorn_recovery_attempted',
        attemptProperties,
      );
    };
    const onPageHide = (): void => {
      emitAttempt();
      this.flushTelemetrySafely();
    };
    this.replaceRecoveryPageHideListener(rung, onPageHide);
    emitAttempt();
    if (!measurable) {
      this.trackEventSafely(
        'unicorn_recovery_outcome',
        createUnicornRecoveryOutcomeProperties(
          pending,
          'unmeasurable',
          this.getNow(),
        )
      );
    }

    void this.executeRecovery(option, measurable);
  }

  async copyIncidentCode(): Promise<void> {
    try {
      const clipboard = this.document.defaultView?.navigator?.clipboard;
      if (!clipboard) {
        throw new Error('clipboard unavailable');
      }
      await clipboard.writeText(this.incidentCode);
      this.recoveryMessage = this.copy.recoveryMessages.incidentCodeCopied;
    } catch {
      this.recoveryMessage = this.copy.recoveryMessages.copyUnavailable;
    }
  }

  private requiresHardRecovery(): boolean {
    return this.staleness === 'stale' ||
      this.cause === 'stale-build' ||
      this.cause === 'unknown';
  }

  private async executeRecovery(
    option: RecoveryOption,
    measurable: boolean,
  ): Promise<void> {
    try {
      let cleanup: UnicornCleanupResult = 'no-op';
      switch (option.action) {
        case 'hard-refresh':
          cleanup = await this.releaseStaleResources();
          this.updatePendingCleanup(option.rung, cleanup);
          this.navigateDocument(option.destination);
          return;
        case 'plain-refresh':
        case 'public-search':
          this.updatePendingCleanup(option.rung, cleanup);
          this.navigateDocument(option.destination);
          return;
        case 'interactive-reauth': {
          this.updatePendingCleanup(option.rung, cleanup);
          const started = await this.settleWithin(
            this.injector.get(UNICORN_REAUTHENTICATE)(option.destination)
          );
          if (started !== true) {
            throw new Error('reauthentication unavailable');
          }
          return;
        }
        case 'reset-auth-session':
          this.updatePendingCleanup(option.rung, cleanup);
          if (!this.clearAndVerifyAuthSession()) {
            this.failRecovery(option, measurable, cleanup);
            return;
          }
          this.navigateDocument(option.destination);
          return;
        case 'network-retry':
          this.updatePendingCleanup(option.rung, cleanup);
          if (!await this.probeConnectivity()) {
            this.connectivity = 'offline';
            this.failRecovery(option, measurable, cleanup);
            this.clearRecoveryGuard(option.rung);
            this.recoveryMessage =
              this.copy.recoveryMessages.connectionUnavailable;
            return;
          }
          this.connectivity = 'online';
          this.navigateDocument(option.destination);
          return;
      }
    } catch {
      this.failRecovery(option, measurable, 'failed');
    }
  }

  private async releaseStaleResources(): Promise<UnicornCleanupResult> {
    const nativeState = this.getNativeState();
    if (nativeState === true) {
      return 'no-op';
    }
    if (nativeState === undefined) {
      return 'unresolved';
    }

    try {
      const view = this.document.defaultView;
      const serviceWorker = view?.navigator?.serviceWorker;
      if (!serviceWorker) {
        return 'no-op';
      }
      const registrations = await this.settleWithin(
        typeof serviceWorker.getRegistrations === 'function'
          ? serviceWorker.getRegistrations()
          : serviceWorker.getRegistration().then(registration =>
            [registration].filter(
              (candidate): candidate is ServiceWorkerRegistration =>
                candidate != null
            )
          )
      );
      if (!registrations) {
        return 'failed';
      }
      if (registrations.length === 0) {
        return 'no-op';
      }
      const angularRegistrations = registrations.filter(registration =>
        this.isAngularServiceWorker(registration)
      );
      if (angularRegistrations.length === 0) {
        return 'no-op';
      }

      const unregisterResults = await this.settleWithin(
        Promise.allSettled(
          angularRegistrations.map(registration => registration.unregister())
        )
      );
      if (!unregisterResults) {
        return 'failed';
      }
      let cacheReleased = true;
      const cacheStorage = view.caches;
      if (cacheStorage) {
        const cacheKeys = await this.settleWithin(cacheStorage.keys());
        if (!cacheKeys) {
          return 'failed';
        }
        const angularCacheKeys = cacheKeys.filter(key =>
          key.startsWith('ngsw:')
        );
        const cacheResults = await this.settleWithin(
          Promise.allSettled(
            angularCacheKeys.map(key => cacheStorage.delete(key))
          )
        );
        if (!cacheResults) {
          return 'failed';
        }
        cacheReleased = cacheResults.every(
          result => result.status === 'fulfilled' && result.value
        );
      }
      const workersReleased = unregisterResults.every(
        result => result.status === 'fulfilled' && result.value
      );
      return workersReleased && cacheReleased ? 'released' : 'failed';
    } catch {
      return 'failed';
    }
  }

  private async probeConnectivity(): Promise<boolean> {
    try {
      const view = this.document.defaultView;
      if (!view?.fetch || view.navigator?.onLine === false) {
        return false;
      }
      const probeUrl = await this.settleWithin(
        this.injector.get(UNICORN_CONNECTIVITY_PROBE_URL)()
      );
      if (!probeUrl) {
        return false;
      }
      const controller = typeof view.AbortController === 'function'
        ? new view.AbortController()
        : undefined;
      const response = await this.settleWithin(
        view.fetch(probeUrl, {
          cache: 'no-store',
          signal: controller?.signal,
        }),
        controller,
      );
      return response?.ok === true;
    } catch {
      return false;
    }
  }

  private navigateDocument(destination: string): void {
    const location = this.document.defaultView?.location;
    if (!location) {
      throw new Error('document navigation unavailable');
    }
    this.documentNavigationInitiated = true;
    location.assign(destination);
  }

  private clearAndVerifyAuthSession(): boolean {
    try {
      const view = this.document.defaultView;
      const storage = view?.localStorage;
      if (!storage) {
        return false;
      }
      const cacheKeys: string[] = [];
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key?.startsWith('@@auth0spajs@@')) {
          cacheKeys.push(key);
        }
      }
      cacheKeys.forEach(key => storage.removeItem(key));

      const cookieNames = this.getAuthCookieNames();
      cookieNames.forEach(name => {
        this.document.cookie =
          `${encodeURIComponent(name)}=; Max-Age=0; Path=/; SameSite=Lax`;
      });

      const cacheRemains = Array.from(
        { length: storage.length },
        (_, index) => storage.key(index)
      ).some(key => key?.startsWith('@@auth0spajs@@'));
      return !cacheRemains && this.getAuthCookieNames().length === 0;
    } catch {
      return false;
    }
  }

  private getAuthCookieNames(): string[] {
    const cookie = this.document.cookie;
    if (!cookie) {
      return [];
    }
    return cookie.split(';')
      .map(part => decodeURIComponent(part.trim().split('=', 1)[0]))
      .filter(name =>
        name === 'auth0.is.authenticated' ||
        (name.startsWith('auth0.') && name.endsWith('.is.authenticated'))
      );
  }

  private persistRecoveryGuard(rung: UnicornRecoveryRung): boolean {
    try {
      const storage = this.document.defaultView?.sessionStorage;
      if (!storage) {
        return false;
      }
      const key = getUnicornRecoveryGuardKey(this.incidentCode, rung);
      const now = this.getNow();
      this.removeExpiredRecoveryGuards(storage, now);
      const previousAttempt = Number(storage.getItem(key));
      if (
        Number.isFinite(previousAttempt) &&
        previousAttempt > 0 &&
        now >= previousAttempt &&
        now - previousAttempt < UNICORN_RECOVERY_GUARD_TTL_MS
      ) {
        return false;
      }

      const persisted = String(now);
      storage.setItem(key, persisted);
      return storage.getItem(key) === persisted;
    } catch {
      return false;
    }
  }

  private removeExpiredRecoveryGuards(storage: Storage, now: number): void {
    const expiredKeys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key?.startsWith(`${UNICORN_RECOVERY_GUARD_PREFIX}.`)) {
        continue;
      }
      const attemptedAt = Number(storage.getItem(key));
      if (
        !Number.isFinite(attemptedAt) ||
        attemptedAt <= 0 ||
        now < attemptedAt ||
        now - attemptedAt >= UNICORN_RECOVERY_GUARD_TTL_MS
      ) {
        expiredKeys.push(key);
      }
    }
    expiredKeys.forEach(key => storage.removeItem(key));
  }

  private clearRecoveryGuard(rung: UnicornRecoveryRung): void {
    if (this.documentNavigationInitiated) {
      return;
    }
    try {
      this.document.defaultView?.sessionStorage?.removeItem(
        getUnicornRecoveryGuardKey(this.incidentCode, rung)
      );
    } catch {
      // A later click will remain safely blocked if the guard cannot be cleared.
    }
  }

  private persistPendingRecovery(pending: UnicornPendingRecovery): boolean {
    try {
      const storage = this.document.defaultView?.sessionStorage;
      if (!storage) {
        return false;
      }
      const previous = parseUnicornPendingRecovery(
        storage.getItem(UNICORN_RECOVERY_PENDING_KEY)
      );
      if (previous) {
        const outcome = this.getNow() - previous.startedAt >=
          UNICORN_RECOVERY_PENDING_TTL_MS
          ? 'expired'
          : 'failure';
        this.trackEventSafely(
          'unicorn_recovery_outcome',
          createUnicornRecoveryOutcomeProperties(
            previous,
            outcome,
            this.getNow(),
          )
        );
      }
      const serialized = JSON.stringify(pending);
      storage.setItem(UNICORN_RECOVERY_PENDING_KEY, serialized);
      return storage.getItem(UNICORN_RECOVERY_PENDING_KEY) === serialized;
    } catch {
      return false;
    }
  }

  private updatePendingCleanup(
    rung: UnicornRecoveryRung,
    cleanup: UnicornCleanupResult,
  ): void {
    try {
      const storage = this.document.defaultView?.sessionStorage;
      const pending = parseUnicornPendingRecovery(
        storage?.getItem(UNICORN_RECOVERY_PENDING_KEY)
      );
      if (
        !storage ||
        !pending ||
        pending.incidentCode !== this.incidentCode ||
        pending.rung !== rung
      ) {
        return;
      }
      storage.setItem(
        UNICORN_RECOVERY_PENDING_KEY,
        JSON.stringify({ ...pending, cleanup })
      );
    } catch {
      // Recovery proceeds; the eventual outcome remains explicitly unresolved.
    }
  }

  private failRecovery(
    option: RecoveryOption,
    measurable: boolean,
    cleanup: UnicornCleanupResult,
  ): void {
    if (measurable) {
      const pending = this.readPendingRecovery(option.rung);
      if (pending) {
        const failed = { ...pending, cleanup };
        this.trackEventSafely(
          'unicorn_recovery_outcome',
          createUnicornRecoveryOutcomeProperties(
            failed,
            'failure',
            this.getNow(),
          )
        );
        this.clearPendingRecovery();
      }
    }
    this.removeRecoveryPageHideListener(option.rung);
    this.recoveryInProgress.delete(option.rung);
    this.recoveryMessage = this.copy.recoveryMessages.actionIncomplete;
  }

  private readPendingRecovery(
    rung: UnicornRecoveryRung
  ): UnicornPendingRecovery | undefined {
    try {
      const pending = parseUnicornPendingRecovery(
        this.document.defaultView?.sessionStorage?.getItem(
          UNICORN_RECOVERY_PENDING_KEY
        )
      );
      return pending?.incidentCode === this.incidentCode &&
        pending.rung === rung
        ? pending
        : undefined;
    } catch {
      return undefined;
    }
  }

  private clearPendingRecovery(): void {
    try {
      this.document.defaultView?.sessionStorage?.removeItem(
        UNICORN_RECOVERY_PENDING_KEY
      );
    } catch {
      // The in-memory latch still prevents another action in this page instance.
    }
  }

  private trackEventSafely(
    name: string,
    properties: Record<string, unknown>,
  ): void {
    try {
      this.injector.get(AppInsightsService).trackEvent(name, properties);
      this.flushTelemetrySafely();
    } catch {
      // Recovery and the fatal surface do not depend on telemetry availability.
    }
  }

  private flushTelemetrySafely(): void {
    try {
      this.injector.get(AppInsightsService).flush();
    } catch {
      // The lifecycle safety path must not recurse when telemetry is unavailable.
    }
  }

  private replaceRecoveryPageHideListener(
    rung: UnicornRecoveryRung,
    listener: EventListener,
  ): void {
    this.removeRecoveryPageHideListener(rung);
    this.recoveryPageHideListeners.set(rung, listener);
    this.addPageHideListener(listener);
  }

  private removeRecoveryPageHideListener(rung: UnicornRecoveryRung): void {
    const listener = this.recoveryPageHideListeners.get(rung);
    if (!listener) {
      return;
    }
    this.removePageHideListener(listener);
    this.recoveryPageHideListeners.delete(rung);
  }

  private getIncidentCode(): string {
    try {
      return this.injector.get(
        UNICORN_INCIDENT_CODE_FACTORY,
        createUnicornIncidentCode
      )();
    } catch {
      return createUnicornIncidentCode();
    }
  }

  private resolveLocale(): FatalPageLocale {
    const readers: Array<() => unknown> = [
      () => this.document.documentElement
        ?.getAttribute(FATAL_PAGE_LANGUAGE_ATTRIBUTE),
      () => this.document.defaultView?.localStorage
        ?.getItem(FATAL_PAGE_LANGUAGE_PREFERENCE_KEY),
      () => this.document.defaultView?.navigator?.language,
    ];

    for (const read of readers) {
      try {
        const value = read();
        if (value === null || value === undefined || value === '') {
          continue;
        }
        return normalizeFatalPageLocale(value) ?? 'en';
      } catch {
        // Continue through progressively weaker locale sources.
      }
    }
    return 'en';
  }

  private isAngularServiceWorker(
    registration: ServiceWorkerRegistration
  ): boolean {
    try {
      const scriptUrl = registration.active?.scriptURL ??
        registration.waiting?.scriptURL ??
        registration.installing?.scriptURL;
      return typeof scriptUrl === 'string' &&
        new URL(scriptUrl, this.document.baseURI).pathname
          .endsWith('/ngsw-worker.js');
    } catch {
      return false;
    }
  }

  private getNow(): number {
    try {
      return Date.now();
    } catch {
      return 0;
    }
  }

  private addPageHideListener(listener: EventListener): void {
    try {
      this.document.defaultView?.addEventListener('pagehide', listener, {
        once: true,
      });
    } catch {
      // The normal bounded path still emits if lifecycle events are unavailable.
    }
  }

  private removePageHideListener(listener: EventListener): void {
    try {
      this.document.defaultView?.removeEventListener('pagehide', listener);
    } catch {
      // Removal failure cannot affect the class-level exactly-once guard.
    }
  }

  private getRunningBuildIdentity(): string {
    try {
      return normalizeUnicornBuildIdentity(
        this.document
          .querySelector<HTMLMetaElement>('meta[name="rettx-build-identity"]')
          ?.content
      );
    } catch {
      return 'unresolved';
    }
  }

  private getNativeState(): boolean | undefined {
    try {
      return Capacitor.isNativePlatform();
    } catch {
      return undefined;
    }
  }

  private async getStalenessVerdict(
    nativeState: boolean | undefined,
    buildIdentity: string,
  ): Promise<UnicornStalenessVerdict> {
    if (nativeState === true) {
      return 'unavailable';
    }
    if (nativeState === undefined || buildIdentity === 'unresolved') {
      return 'unresolved';
    }

    try {
      const view = this.document.defaultView;
      if (!view?.fetch) {
        return 'unresolved';
      }

      const controller = typeof view.AbortController === 'function'
        ? new view.AbortController()
        : undefined;
      const response = await this.settleWithin(
        view.fetch('/build-info.json', {
          cache: 'no-store',
          signal: controller?.signal,
        }),
        controller,
      );
      if (!response?.ok) {
        return 'unresolved';
      }

      const body = await this.settleWithin(response.json(), controller);
      const servedIdentity = normalizeUnicornBuildIdentity(
        this.readBuildIdentity(body)
      );
      if (servedIdentity === 'unresolved') {
        return 'unresolved';
      }
      return servedIdentity === buildIdentity ? 'fresh' : 'stale';
    } catch {
      return 'unresolved';
    }
  }

  private async getServiceWorkerState(
    nativeState: boolean | undefined
  ): Promise<UnicornServiceWorkerState> {
    if (nativeState === true) {
      return 'disabled';
    }
    if (nativeState === undefined) {
      return 'unresolved';
    }

    try {
      const serviceWorker = this.document.defaultView?.navigator?.serviceWorker;
      if (!serviceWorker) {
        return 'unsupported';
      }

      const registration = await this.settleWithin(
        serviceWorker.getRegistration()
      );
      if (!registration) {
        return 'disabled';
      }
      if (registration.waiting) {
        return 'waiting';
      }
      if (registration.installing) {
        return 'installing';
      }
      if (registration.active) {
        return 'active';
      }
      return 'disabled';
    } catch {
      return 'unresolved';
    }
  }

  private getConnectivityState(): UnicornConnectivityState {
    try {
      const online = this.document.defaultView?.navigator?.onLine;
      return typeof online === 'boolean'
        ? online ? 'online' : 'offline'
        : 'unresolved';
    } catch {
      return 'unresolved';
    }
  }

  private readBuildIdentity(value: unknown): unknown {
    if (typeof value !== 'object' || value === null) {
      return undefined;
    }
    return (value as Record<string, unknown>)['build_identity'];
  }

  private async settleWithin<T>(
    operation: Promise<T>,
    controller?: AbortController,
  ): Promise<T | undefined> {
    const timeoutMs = this.getProbeTimeout();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutResult = new Promise<undefined>(resolve => {
      timeoutId = setTimeout(() => {
        try {
          controller?.abort();
        } catch {
          // The timeout still resolves even if abort is unavailable.
        }
        resolve(undefined);
      }, timeoutMs);
    });

    try {
      return await Promise.race([
        operation,
        timeoutResult,
      ]);
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }
  }

  private getProbeTimeout(): number {
    try {
      const configured = this.injector.get(UNICORN_PROBE_TIMEOUT_MS);
      return Number.isFinite(configured) && configured >= 0
        ? configured
        : 3000;
    } catch {
      return 3000;
    }
  }

  private getHandoffState(): Record<string, unknown> | undefined {
    try {
      const navigationState = this.injector.get(Router, null)
        ?.getCurrentNavigation()
        ?.extras.state;
      if (navigationState) {
        return navigationState;
      }
    } catch {
      // Fall through to the browser history state.
    }

    try {
      return this.document.defaultView?.history.state;
    } catch {
      return undefined;
    }
  }
}
